import { useEffect, useMemo, useRef } from 'react';
import { usePersistedWidth } from '../hooks/usePersistedWidth';
import { AttachFileButton } from './AttachFileButton';
import { AttachErrorAlert } from './AttachErrorAlert';
import { composerChrome } from './composerChrome.css';
import { AttachmentChips } from './AttachmentChips';
import { OptionChips, type ChipOption } from './OptionChips';
import { TurnActivity } from './TurnActivity';
import {
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  ResizablePane,
  StatusTag,
  theme,
  type Message,
  type ComposerMentions,
  type MentionMatcher,
} from '@agentisai/design-system';
import { type TurnEvent, type WorkstreamMode } from '@agentisai/shared-types';

/**
 * The platform agents a user may invite into a workstream conversation
 * (mirrors `apps/workstreamagent/api/agents/platform/*`). Hand-maintained until
 * the registry is exposed to the BFF (follow-on).
 *
 * `discovery` is listed first and deliberately: it runs the pre-creation
 * interview, so a user who has seen it answer expects to find it here, and
 * after a `no_build` exit re-opening the interview ("actually, I do want
 * something built") is a real thing to want. The pipeline routes a forced
 * `discovery` like any other agent once the workstream is out of discovery
 * mode; while it IS in discovery mode this row is replaced by the
 * Exit-Discovery control, so there is no way to invite it into itself.
 */
export const INVITABLE_AGENTS: readonly { name: string; label: string }[] = [
  { name: 'discovery', label: 'Discovery' },
  { name: 'architect', label: 'Architect' },
  { name: 'delivery', label: 'Delivery' },
  { name: 'engineer', label: 'Engineer' },
  { name: 'governance', label: 'Governance' },
  { name: 'qa', label: 'QA' },
];

/**
 * Display label for an agent name. Falls back to capitalising the raw name, so
 * an agent outside the invitable roster (`discovery`, `librarian`) still reads
 * as a name rather than a slug. Exported because the transcript renderer labels
 * per-message speakers with it too (ADR-0054).
 */
export const agentLabel = (name: string): string =>
  INVITABLE_AGENTS.find((a) => a.name === name)?.label ??
  name.charAt(0).toUpperCase() + name.slice(1);

/**
 * Front-of-house roles: they interview and route rather than build. Kept on the
 * purple `mira` treatment — the tone the create-flow interview already uses.
 *
 * `librarian` is RETIRED as a runtime agent (ADR-0066 §3) and nothing will ever
 * stamp it again — it stays here because every workstream created before that
 * carries `librarian`-stamped turns, and dropping it would repaint their
 * avatars blue on the next render. Historical transcripts are the reason, not
 * live routing.
 */
const COORDINATION_AGENTS = new Set(['discovery', 'librarian', 'forge']);

/**
 * Bubble variant for an assistant turn, which is what colours the avatar ring.
 * Every reply used to render `mira`, so six agents were visually identical and
 * the name was the only clue — and two of them ('Discovery' / 'Delivery') even
 * share an avatar initial. Splitting coordination (purple) from specialists
 * (blue) makes "who answered this" legible before you read anything.
 */
export function agentVariant(agents: readonly string[] | undefined): 'mira' | 'agent' {
  const first = agents?.[0];
  return first !== undefined && !COORDINATION_AGENTS.has(first) ? 'agent' : 'mira';
}

/**
 * Small circular initial chip for a mention row — soft-tinted by role (blue
 * specialist / purple coordination), matching the Genesis soft-fill vocabulary.
 */
function agentAvatar(name: string, initial: string) {
  const specialist = agentVariant([name]) === 'agent';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: theme.radii.circle,
        background: specialist ? theme.color.accent.blue[100] : theme.color.accent.purple[50],
        color: specialist ? theme.color.accent.blue[700] : theme.color.accent.purple[700],
        fontSize: theme.typography.fontSize.caption,
        fontWeight: theme.typography.fontWeight.bold,
        lineHeight: 1,
      }}
    >
      {initial}
    </span>
  );
}

/** Mention-row chip for a custom agent — the purple of its elevated bubble (ADR-0080 D7). */
function customAgentAvatar(initial: string) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: theme.radii.circle,
        background: theme.color.accent.purple[500],
        color: theme.color.neutral.white,
        fontSize: theme.typography.fontSize.caption,
        fontWeight: theme.typography.fontWeight.bold,
        lineHeight: 1,
      }}
    >
      {initial}
    </span>
  );
}

/**
 * The invitable roster as Composer `@mention` items — alphabetical by label,
 * each with its initial chip. Same source as the invite buttons (single source
 * of truth), so the two stay in lockstep.
 */
const MENTION_AGENTS = [...INVITABLE_AGENTS]
  .sort((a, b) => a.label.localeCompare(b.label))
  .map((a) => ({ id: a.name, label: a.label, icon: agentAvatar(a.name, a.label.charAt(0)) }));

export interface WorkspaceConversationProps {
  title: string;
  /**
   * Passed through to `ChatPanel`. Default true (full-pane conversation);
   * false when rendered as the canonical 40% sibling column beside the
   * centre-pane file viewer (the DS prototype shell layout).
   */
  fluid?: boolean;
  messages: Message[];
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  maxLength?: number;
  pending?: boolean;
  sendError?: string | null;
  /** Currently selected invite-agent hint, or `null` for router-auto. */
  invitedAgent: string | null;
  onInviteAgent: (agentName: string | null) => void;
  /**
   * Librarian routing mode, as last reported by the server. `discovery`
   * locks the conversation to the discovery interview — the invite row is
   * replaced by the exit affordance until the user ends the interview.
   */
  mode?: WorkstreamMode;
  /**
   * Agents that produced the latest reply, in execution order (server-reported
   * `data.agents`). Staged turns read as a pipeline (e.g. Engineer → QA).
   */
  activeAgents?: readonly string[];
  /** Ends the discovery interview (sends the `exitDiscovery` action). */
  onExitDiscovery?: () => void;
  /**
   * Live progress checkpoints for the in-flight turn (ADR-0042) — the latest
   * renders as a status line while `pending`; the full trail sits behind a
   * disclosure. Cleared by the parent when the turn resolves.
   */
  turnEvents?: readonly TurnEvent[];
  /**
   * Consensus-tracker conflict detail for the latest turn, or `null` when the
   * agents agreed. Rendered as a warning banner (Librarian flow step 8b —
   * "surface conflict to user").
   */
  conflict?: string | null;
  /**
   * Files staged for the next turn (parent-owned, like the composer draft).
   * Staged, not uploaded — the parent flushes them on send.
   */
  stagedFiles: readonly File[];
  /** Files were picked. Validation + staging are the parent's job. */
  onFilesPicked: (files: File[]) => void;
  /** Drop one staged file before sending. */
  onRemoveFile: (file: File) => void;
  /** Name of the file currently uploading during a flush, or `null`. */
  uploadingFilename?: string | null;
  /** Upload progress 0–100, meaningful only while {@link uploadingFilename} is set. */
  uploadProgress?: number;
  /** Attach/upload failure copy, already user-facing. */
  attachError?: string | null;
  /** Clear {@link attachError}. */
  onDismissAttachError: () => void;
  /**
   * The workstream's stored files, for the composer's `/` reference typeahead
   * (ADR-0060) — the SAME `useArtifacts` list the Files rail renders, passed
   * down rather than re-queried so the rail and the typeahead can never
   * disagree about what exists. Empty / omitted disables the `/` trigger.
   */
  files?: readonly { artifactId: string; filename: string }[];
  /**
   * Session-scoped acknowledgement that the workstream was just created.
   * Chrome, NOT a transcript message — a scripted bubble under the agent's
   * live question is the defect ADR-0059 removed. The parent owns its lifetime
   * and clears it on the next send. A flag, not copy: the Callout's title says
   * the whole thing, and a body sentence under it was too heavy on screen for
   * a transient acknowledgement.
   */
  showCreatedNotice?: boolean;
  /** Numbered options the agent just offered, as one-click answers (via `onSend`). */
  options?: readonly ChipOption[];
  /**
   * ADR-0080: this workstream's member agents (custom/curated, run by
   * AgentForge). They join the `@` typeahead beside the platform roster.
   */
  memberAgents?: readonly CustomAgentRef[];
  /** The org's shared agents not yet in this workstream — offered as "Add …". */
  invitableAgents?: readonly CustomAgentRef[];
  /** Invite / remove a member agent. Omitted: the controls aren't shown. */
  onAddAgent?: (agentId: string) => void;
  onRemoveAgent?: (agentId: string) => void;
  /** An invite or remove is in flight — its buttons disable (double-submit guard). */
  agentChangePending?: boolean;
  /**
   * A builder workstream (ADR-0080, AB-2-2): the BFF pins every turn to Forge, so
   * the `@` agent trigger, the invite/add/remove buttons and the routing status
   * have nothing to switch and are left out.
   */
  builder?: boolean;
}

/** A custom agent as the conversation needs it: the id the server routes on, the name people see. */
export interface CustomAgentRef {
  agentId: string;
  displayName: string;
}

/**
 * WorkspaceConversation — the live conversation for an already-created
 * workstream (post-creation). Composes the same DS primitives as
 * `CreateWorkstreamPanel` (ChatPanel + MessageBubble + Composer), rendered
 * `fluid` inline in the WorkspaceShell main pane rather than in a Modal.
 * State is fully owned by the parent (HomePage) — this component renders only.
 */
export function WorkspaceConversation({
  title,
  fluid = true,
  messages,
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  sendError,
  invitedAgent,
  onInviteAgent,
  mode = 'normal',
  activeAgents = [],
  turnEvents = [],
  onExitDiscovery,
  conflict,
  stagedFiles,
  onFilesPicked,
  onRemoveFile,
  uploadingFilename = null,
  uploadProgress = 0,
  attachError = null,
  onDismissAttachError,
  files = [],
  showCreatedNotice = false,
  options = [],
  memberAgents = [],
  invitableAgents = [],
  onAddAgent,
  onRemoveAgent,
  agentChangePending = false,
  builder = false,
}: WorkspaceConversationProps) {
  // Platform roster + this workstream's member agents, one typeahead (ADR-0080).
  // Memoised: it feeds the typeahead's `mentions` config, which must not change
  // identity on every parent render while the list is open.
  const mentionAgents = useMemo(
    () =>
      [
        ...MENTION_AGENTS,
        ...memberAgents.map((a) => ({
          id: a.agentId,
          label: a.displayName,
          icon: customAgentAvatar(a.displayName.charAt(0)),
        })),
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [memberAgents],
  );
  const labelFor = (name: string): string =>
    memberAgents.find((a) => a.agentId === name)?.displayName ?? agentLabel(name);
  // Auto-scroll to the latest turn — same recipe as CreateWorkstreamPanel
  // (imperative DOM sync, the sanctioned effect+ref use, frontend-architecture R24/R25).
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  // @mention agent typeahead (DS Composer `mentions` capability): type "@" to
  // address the turn to a specialist — the same soft-hint path as clicking an
  // invite button below. Prefix-filtered on the label; selecting sets the
  // invited agent (single, last wins). Disabled in discovery mode (routing is
  // locked; the invite row is hidden then) by passing `undefined`.
  const agentMentions = useMemo<ComposerMentions>(
    () => ({
      listLabel: 'Agents',
      getItems: (query) => {
        const q = query.toLowerCase();
        return mentionAgents.filter((a) => a.label.toLowerCase().startsWith(q));
      },
      onSelect: (item) => onInviteAgent(item.id),
    }),
    [onInviteAgent, mentionAgents],
  );

  /**
   * `/file` reference typeahead (ADR-0060) — the second trigger over the same
   * composer. Selecting one commits a `/filename` chip into the message text;
   * nothing new rides the wire, because every agent already receives the
   * artifact ledger (filename + key) in its system prompt and the talking
   * agents hold `read_artifact`.
   *
   * `labelChars` widens the token past the `.` and `-` a filename carries —
   * per trigger, so `@` keeps ending at the first dot. A filename containing a
   * SPACE is deliberately out of scope: it lands as plain text, which still
   * names the file to the model, just without the chip.
   */
  const fileMentions = useMemo<ComposerMentions>(
    () => ({
      trigger: '/',
      labelChars: '.-',
      listLabel: 'Files',
      getItems: (query) => {
        const q = query.toLowerCase();
        return files
          .filter((f) => f.filename.toLowerCase().startsWith(q))
          .map((f) => ({
            id: f.artifactId,
            label: f.filename,
            icon: <Icon as={icons.FileText} size={14} aria-hidden />,
          }));
      },
    }),
    [files],
  );

  // Agents AND files share one composer. Discovery mode drops the agent
  // trigger (routing is server-locked, so an invite is inert) but keeps files —
  // a workstream in a discovery interview can still hold an uploaded exemplar.
  // With no files the `/` config is left OUT rather than registered against an
  // empty list. Not observable from the DOM — an empty `getItems` renders no
  // options either way — so this is the prop's documented contract ("empty /
  // omitted disables the `/` trigger") made true in the code, and it keeps the
  // per-rebuild `/` scan off a composer with nothing to reference.
  const composerMentions = useMemo(() => {
    const configs = mode === 'discovery' || builder ? [] : [agentMentions];
    return files.length > 0 ? [...configs, fileMentions] : configs;
  }, [mode, builder, files.length, agentMentions, fileMentions]);

  /**
   * The same two triggers, applied to the SENT transcript: `@Engineer` and
   * `/prd.md` render as the chip they were in the composer instead of flat
   * text. Built from the raw rosters rather than from `composerMentions`,
   * because that list is mode- and file-conditional — the composer drops the
   * `@` trigger in discovery mode, and a message already sent should not lose
   * its chips when the composer's configuration changes underneath it.
   *
   * `isMention` is what keeps this honest: an unknown `@someone` or a `/file`
   * that has since been removed stays plain text rather than pretending to be
   * a live reference.
   */
  const transcriptMentions = useMemo<readonly MentionMatcher[]>(
    () => [
      {
        trigger: '@',
        isMention: (word) => mentionAgents.some((a) => a.label === word),
      },
      {
        trigger: '/',
        labelChars: '.-',
        isMention: (word) => files.some((f) => f.filename === word),
      },
    ],
    [files, mentionAgents],
  );

  // Cursor-style mode indicator: which routing mode the Librarian is in, and
  // which agents produced the latest reply (in execution order — a staged
  // build→review turn reads Engineer → QA).
  const statusRow = (
    // role=group (not status): a live region here would announce every turn,
    // and the conflict banner below is the page's one role=status element.
    <div
      role="group"
      aria-label="Conversation status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.space['1_5'],
        paddingInline: theme.space[4],
      }}
    >
      {builder ? (
        <StatusTag tone="info">Building with Forge</StatusTag>
      ) : mode === 'discovery' ? (
        <StatusTag tone="info">Discovery mode</StatusTag>
      ) : invitedAgent ? (
        <StatusTag tone="info">Invited: {labelFor(invitedAgent)}</StatusTag>
      ) : (
        <StatusTag tone="neutral">Auto routing</StatusTag>
      )}
      {activeAgents.length > 0 && (
        <StatusTag tone="success">Last reply: {activeAgents.map(agentLabel).join(' → ')}</StatusTag>
      )}
    </div>
  );

  // Reaches the Composer's Lexical contenteditable for focus only
  // (frontend-architecture R25). See `startMention` below.
  const composerRef = useRef<HTMLDivElement | null>(null);

  /**
   * "Invite an agent" — append `@` and focus the editor, which is the gesture
   * the typeahead is built around. The leading space matters: its trigger regex
   * only matches an `@` at line start or after whitespace.
   *
   * ponytail: this INSERTS THE TRIGGER rather than commanding the menu open,
   * which works because `LexicalTypeaheadMenuPlugin` opens off an editor UPDATE
   * listener — coupling to plugin internals, not a contract (a HomePage test
   * asserts the full listbox opens, so a regression fails there). Upgrade path
   * is an imperative `openMentions()` on the DS Composer, deliberately NOT
   * added: a DS API minted for one app-layer button is the wrong trade.
   */
  const focusComposer = () =>
    composerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();

  const startMention = (trigger: string) => {
    const draft = value ?? '';
    onChange?.(
      draft.length === 0 || draft.endsWith(' ') ? `${draft}${trigger}` : `${draft} ${trigger}`,
    );
    focusComposer();
  };

  const composerLeadingActions = (
    <>
      <AttachFileButton onFilesPicked={onFilesPicked} disabled={uploadingFilename !== null} />
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  const [chatWidth, setChatWidth, persistChatWidth] = usePersistedWidth('agentis.chatPanelWidth');
  const panel = (
    <ChatPanel
      fluid={fluid}
      caption={
        builder
          ? 'Describe the agent you want — Forge drafts it and saves each revision'
          : mode === 'discovery'
            ? 'Discovery interview in progress — the discovery agent leads until you exit'
            : files.length > 0
              ? 'Ask a question, @mention a specialist agent, or /reference a file'
              : 'Ask a question, or @mention a specialist agent'
      }
      title={title}
      awaitingReply={pending}
      // Name who is about to answer. The DS default is "Workstream Agent is
      // typing", which showed on EVERY turn — including the ones a named
      // specialist handled. Left undefined under auto-routing, where the
      // Librarian genuinely has not picked an agent yet: the generic default is
      // the honest label there, not a guess.
      typingLabel={
        builder
          ? 'Forge is typing'
          : mode === 'discovery'
            ? 'Discovery is typing'
            : invitedAgent
              ? `${agentLabel(invitedAgent)} is typing`
              : undefined
      }
      messages={
        <>
          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              mentions={transcriptMentions}
              // 'user' is HomePage's memberId for the signed-in user's turns.
              isSelf={m.memberId === 'user'}
              // Consecutive messages from one sender share the first one's name
              // and avatar. Keyed on memberName, not memberId: memberId is only
              // agents[0], so 'Librarian → Engineer' and 'Librarian → Designer'
              // would merge. ponytail: sender-only, add a time gap if long
              // pauses between same-sender messages need their own header.
              showMeta={messages[i - 1]?.memberName !== m.memberName}
            />
          ))}
          <div ref={bottomRef} aria-hidden="true" />
        </>
      }
      composer={
        <>
          <div className={composerChrome}>
            {statusRow}
            {showCreatedNotice && (
              // Polite live region, same as the conflict banner below. Chrome,
              // not a MessageBubble — it never reaches the model as a turn.
              // Title-only: the icon + "Workstream created" is the entire
              // message, and the conversation carrying on below shows the rest.
              <div role="status" aria-live="polite" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="success" title="Workstream created" />
              </div>
            )}
            {/* Live turn narration (ADR-0042) — status line + trail, see TurnActivity. */}
            {pending && <TurnActivity events={turnEvents} />}
            {builder && files.length === 0 ? null : mode === 'discovery' && !builder ? (
              // Discovery locks routing server-side — inviting is inert, so the
              // row is replaced by the one meaningful action: exit the interview.
              // If this chrome grows beyond a single button (banner, distinct
              // composer state), promote it to a DS ChatPanel variant + story (R0).
              <div
                style={{
                  display: 'flex',
                  gap: theme.space['1_5'],
                  paddingInline: theme.space[4],
                }}
              >
                <Button size="sm" variant="secondary" disabled={pending} onClick={onExitDiscovery}>
                  Exit Discovery Interview
                </Button>
              </div>
            ) : (
              // The static roster of six agent chips is gone — the composer's
              // `@` typeahead selects an agent. Exactly one button survives,
              // never six: RELEASING an invite is something a mention cannot
              // express, and `@` is invisible until typed, so with no invite
              // held this region would otherwise advertise nothing at all.
              // role=group + a name, as the six-chip roster had. It is the only
              // thing naming this region to a screen reader, and the `@` / `/`
              // triggers that replaced the chips are invisible until typed —
              // so dropping the name left nothing to announce.
              <div
                role="group"
                aria-label="Agents and files"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.space['1_5'],
                  paddingInline: theme.space[4],
                }}
              >
                {builder ? null : invitedAgent ? (
                  <Button size="sm" variant="ghost" onClick={() => onInviteAgent(null)}>
                    Back to auto routing
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => startMention('@')}>
                    @ Invite an agent
                  </Button>
                )}
                {!builder &&
                  onAddAgent &&
                  invitableAgents.map((a) => (
                    // ADR-0080: one button per shared agent the org can add —
                    // a handful in AB-1. ponytail: AB-2's library replaces this
                    // row once an org has more agents than fit on one line.
                    <Button
                      key={`add-${a.agentId}`}
                      size="sm"
                      variant="ghost"
                      disabled={agentChangePending}
                      onClick={() => onAddAgent(a.agentId)}
                    >
                      + Add {a.displayName}
                    </Button>
                  ))}
                {!builder &&
                  onRemoveAgent &&
                  memberAgents.map((a) => (
                    <Button
                      key={`remove-${a.agentId}`}
                      size="sm"
                      variant="ghost"
                      disabled={agentChangePending}
                      onClick={() => onRemoveAgent(a.agentId)}
                    >
                      Remove {a.displayName}
                    </Button>
                  ))}
                {files.length > 0 && (
                  // Same reason `@` earned a button: the trigger is invisible
                  // until typed. Hidden with no files rather than shown inert —
                  // an affordance whose list would be empty teaches nothing.
                  <Button size="sm" variant="ghost" onClick={() => startMention('/')}>
                    / Reference a file
                  </Button>
                )}
              </div>
            )}
            {onSend && <OptionChips options={options} onPick={onSend} />}
            <AttachmentChips
              files={stagedFiles}
              onRemove={onRemoveFile}
              uploadingName={uploadingFilename}
              uploadProgress={uploadProgress}
            />
            {attachError && (
              <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                <AttachErrorAlert
                  message={attachError}
                  onDismiss={() => {
                    onDismissAttachError();
                    // The ✕ unmounts with the alert, so focus would fall to
                    // <body> and the next Tab would restart at the top of the
                    // document. Put it back where the user was drafting.
                    focusComposer();
                  }}
                />
              </div>
            )}
            {conflict && (
              // Advisory, not an error: polite live region so SRs announce the
              // disagreement without interrupting (Callout carries no role itself).
              <div role="status" aria-live="polite" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="warn" title="Agents disagreed on this reply">
                  {conflict}
                </Callout>
              </div>
            )}
            {sendError && (
              <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="danger">{sendError}</Callout>
              </div>
            )}
          </div>
          <Composer
            // Without this the ref above is permanently null and `startMention`'s focus
            // call is a silent no-op — the trigger char is inserted but focus
            // stays on the button, so the arrow keys never reach the listbox.
            ref={composerRef}
            value={value}
            onChange={onChange}
            onSend={onSend}
            sendDisabled={sendDisabled}
            prominence="spotlight"
            mentions={composerMentions}
            placeholder={
              builder
                ? 'Tell Forge about the agent…'
                : mode === 'discovery'
                  ? 'Reply to the discovery agent…'
                  : invitedAgent
                    ? `Ask ${labelFor(invitedAgent)}…`
                    : files.length > 0
                      ? 'Ask a question, @ an agent, / a file…'
                      : 'Ask a question, or @mention an agent…'
            }
            leadingActions={composerLeadingActions}
          />
          {maxLength != null && (value?.length ?? 0) > maxLength && (
            // Below the Composer, so outside `composerChrome` — it owns its own
            // top gap rather than inheriting the chrome column's rhythm.
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">
                Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                {maxLength.toLocaleString()} characters. Shorten it to send.
              </Callout>
            </div>
          )}
        </>
      }
    />
  );

  // Fluid = a full-pane conversation, nothing to resize against, so the pane is
  // `disabled` rather than absent — ChatPanel keeps its place in the tree across
  // the tier flip instead of remounting. Beside the file viewer it is the 40%
  // column, and the user can drag it wider — the pane keeps 320px free for the viewer.
  return (
    <ResizablePane
      edge="start"
      handleLabel="Resize conversation panel"
      min={theme.size.chatPanelMinWidth}
      max={720}
      defaultWidth="40%"
      reserve={320}
      width={chatWidth}
      onWidthChange={setChatWidth}
      onResizeEnd={persistChatWidth}
      disabled={fluid}
    >
      {panel}
    </ResizablePane>
  );
}
WorkspaceConversation.displayName = 'WorkspaceConversation';
